import { useState, useEffect } from 'react'

export type DeviceType = 'mobile' | 'tablet' | 'desktop'

export interface DeviceInfo {
  type: DeviceType
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  screenWidth: number
  screenHeight: number
  isTouchDevice: boolean
}

export const useDeviceType = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
    // Initialize with default values (SSR safe)
    if (typeof window === 'undefined') {
      return {
        type: 'desktop',
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        screenWidth: 1024,
        screenHeight: 768,
        isTouchDevice: false
      }
    }

    return getDeviceInfo()
  })

  useEffect(() => {
    const handleResize = () => {
      setDeviceInfo(getDeviceInfo())
    }

    // Set initial device info
    setDeviceInfo(getDeviceInfo())

    // Listen for window resize
    window.addEventListener('resize', handleResize)
    
    // Listen for orientation change (mobile devices)
    window.addEventListener('orientationchange', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
    }
  }, [])

  return deviceInfo
}

function getDeviceInfo(): DeviceInfo {
  const screenWidth = window.innerWidth
  const screenHeight = window.innerHeight
  
  // Check if device supports touch
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0

  // Determine device type based on screen width and touch capability
  let type: DeviceType
  
  if (screenWidth < 768) {
    type = 'mobile'
  } else if (screenWidth < 1024) {
    type = 'tablet'
  } else {
    type = 'desktop'
  }

  // Special handling for mobile devices that might have larger screens
  if (isTouchDevice && screenWidth < 1024) {
    type = screenWidth < 768 ? 'mobile' : 'tablet'
  }

  // Check user agent for additional mobile detection
  const userAgent = navigator.userAgent.toLowerCase()
  const mobileKeywords = ['mobile', 'android', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone']
  const isMobileUA = mobileKeywords.some(keyword => userAgent.includes(keyword))

  // Override type if user agent indicates mobile but screen is large
  if (isMobileUA && type === 'desktop') {
    type = 'tablet'
  }

  return {
    type,
    isMobile: type === 'mobile',
    isTablet: type === 'tablet',
    isDesktop: type === 'desktop',
    screenWidth,
    screenHeight,
    isTouchDevice
  }
}

// Helper hook for specific device checks
export const useIsMobile = () => {
  const { isMobile } = useDeviceType()
  return isMobile
}

export const useIsDesktop = () => {
  const { isDesktop } = useDeviceType()
  return isDesktop
}

export const useIsTablet = () => {
  const { isTablet } = useDeviceType()
  return isTablet
} 